/**
 * WHAT SHE TOLD THE OFFICE SHE SENT, KEPT WHILE HER REPORT THAT SHE SENT IT BY ZELLE HOLDS THE
 * INVOICE ON ITS WAY (round four, slice M1; reported to ops since round five's slice B2, ops's
 * section 11.7, P53; a record since round six, slice S1; and her own way to take it back since ops's
 * 11.7, "Taking it back"), on an invoice's own page, in the place of the ways to pay, under "How did
 * we do?": "What you told us you sent" (`ZELLE.whatYouSentHeading`, NEW), then the tag and the memo
 * as a fact, as the step she answered named them (`whatYouSent`), "A Zelle payment to faithfullykept
 * with the memo FK-42."; then her way to take it back. The owner's walk of 2026-10-17 asked for the
 * details to stay once she had said she sent it; the replay of 2026-10-18 found them still read as an
 * ask to send the amount, with Copy beside the tag, which a hurried client could take as a second ask
 * and pay twice, and "Sent it by mistake?" read as money sent by mistake, not a word given by mistake.
 * So they read as a record: no Copy, no step to send.
 *
 * - "I HAVE NOT SENT IT YET" (`ZELLE.notSentYet`, NEW), a text link, once ops holds her report:
 *   pressed, the page sends `invoice.zelle_withdrawn` (ops's 11.7, "Taking it back", live on both of
 *   its hosts from 2026-10-19). Then, in its place, in a live region: "Telling the office you have
 *   not sent it yet." while ops has not answered; what came of it where a report may still stand,
 *   in ops's words or home's own line, with the phone. Where nothing holds the invoice any more, the
 *   page says so above the ways to pay, and this is not drawn.
 * - WHILE OPS HAS NOT ANSWERED HER REPORT, there is nothing at ops to take back yet, so "Told us this
 *   by mistake, or not sent it yet?" gives the office's number in its place
 *   (`ZELLE.toldByMistakeBeforePhone`, NEW), for the few seconds before ops answers.
 * - NO AMOUNT. Ops keeps only the day she said she sent it (section 11.7), and what is still to pay
 *   moves under her report while it stands, by the office's adjustment or a match of part of a split
 *   Zelle, so the balance said here would be an amount she never said. What is still to pay is ops's
 *   figure on the card above, and nothing here.
 * - ANOTHER PERSON'S REPORT, and one ops holds that home has no record of: no record and no link,
 *   since only the person who made it may take it back (ops's `reported_by_another`), and "Not sent
 *   after all?" with the office's number (`ZELLE.notSentAfterAllBeforePhone`, NEW); or, where ops
 *   refused her own press saying the report is another person's, ops's words in its place.
 * - WITHOUT A TAG FROM OPS, as where ops stops offering Zelle on it, the record is left out.
 * - It never says paid: the invoice reads paid only once ops pushes it so (binding rule 1).
 */
import { useId, type ReactNode, type Ref } from 'react';
import type { Zelle } from '../api.ts';
import { whatYouSent, ZELLE } from '../words.ts';
import { TEXT_LINK } from './buttons.ts';
import { Phone } from './Phone.tsx';
import { keptWhole, MEMO_WHOLE } from './Zelle.tsx';

/** Her way to take her report back, as the invoice's page gives it (11.7, "Taking it back"). */
export interface TakeBackOffer {
  /** The link is drawn: ops holds her own report, and nothing has come of a taking back. */
  readonly offered: boolean;
  /** Takes it back. */
  readonly onPress: () => void;
  /** Her press is on its way to the request door, so another does nothing. */
  readonly pressing: boolean;
  /** Ops has not answered her taking it back yet, which is said in place of the link. */
  readonly sending: boolean;
  /** What came of it while a report may still stand, said in place of the link, with the phone. */
  readonly said?: ReactNode;
  /** What did not go through at the door, under the link, with the phone. */
  readonly problem?: ReactNode;
  /** The line said in place of the link, which takes the focus where it came of her press. */
  readonly lineRef?: Ref<HTMLParagraphElement>;
}

interface ZelleSentProps {
  /** Ops's Zelle details on the invoice, or none where it offers no Zelle on it now. */
  readonly zelle: Zelle | undefined;
  /** Her own person made the report, so what she told the office is kept, and hers to take back. */
  readonly own: boolean;
  readonly takeBack: TakeBackOffer;
}

const LINE = 'text-body text-pretty';

export function ZelleSent({ zelle, own, takeBack }: ZelleSentProps) {
  const headingId = useId();
  if (!own) {
    return (
      <div aria-live="polite">
        <p ref={takeBack.lineRef} tabIndex={-1} className={LINE}>
          {takeBack.said ?? (
            <>
              {ZELLE.notSentAfterAllBeforePhone} <Phone after="." />
            </>
          )}
        </p>
      </div>
    );
  }
  const sent = zelle === undefined ? null : whatYouSent(zelle.tag, zelle.memo);
  // In place of the link: what came of her taking it back, or that it is on its way to the office.
  const inPlace = takeBack.said ?? (takeBack.sending ? ZELLE.takingBack : null);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <h2 id={headingId} className="font-serif text-h3 font-semibold text-pretty text-text-body">
        {ZELLE.whatYouSentHeading}
      </h2>
      {zelle === undefined || sent === null ? null : (
        <div className="rounded-card border border-border-subtle bg-surface-raised p-5">
          <p className="text-body text-pretty wrap-break-word">
            {zelle.memo === undefined
              ? sent
              : keptWhole(sent, [{ words: `${zelle.memo}.`, className: MEMO_WHOLE }])}
          </p>
        </div>
      )}
      {inPlace === null && takeBack.offered ? (
        <button
          type="button"
          aria-disabled={takeBack.pressing ? true : undefined}
          onClick={() => {
            if (!takeBack.pressing) takeBack.onPress();
          }}
          className={`self-start cursor-pointer text-body font-semibold aria-disabled:cursor-wait aria-disabled:opacity-70 ${TEXT_LINK}`}
        >
          {ZELLE.notSentYet}
        </button>
      ) : null}
      {inPlace === null && !takeBack.offered ? (
        <p className={LINE}>
          {ZELLE.toldByMistakeBeforePhone} <Phone after="." />
        </p>
      ) : null}
      <div aria-live="polite">
        {inPlace !== null ? (
          <p ref={takeBack.lineRef} tabIndex={-1} className={LINE}>
            {inPlace}
          </p>
        ) : takeBack.offered ? (
          (takeBack.problem ?? null)
        ) : null}
      </div>
    </section>
  );
}
